import { useEffect, useState } from 'react';
import { fetchCrony, type CronyJob, type CronyOverview, type CronyPill } from '../api/crony';
import { RefreshCw } from 'lucide-react';
import { ArcPage, ArcPageHeader, ArcPanel } from '../components/arc/primitives/arc-primitives';
import { Button } from '../components/arc/button/button';
import { PageState } from '../components/arc/page-state/page-state';
import './crony.css';

const PILL_LABEL: Record<CronyPill, string> = {
  online: 'Aktivní',
  warning: 'Zpoždění',
  offline: 'Výpadek',
  unknown: 'Neznámý',
};
const SPACE_LABEL: Record<string, string> = { rondo: 'Rondo', affilmaster: 'Affilmaster', all: 'Napříč' };

function relTime(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return '-';
  if (ms < 0) return 'právě teď';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'právě teď';
  if (min < 60) return `před ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `před ${h} h`;
  const d = Math.floor(h / 24);
  return `před ${d} dny`;
}

function CronyCard({ job }: { job: CronyJob }) {
  return (
    <ArcPanel as="article" className={`crony-card status-${job.pill}`}>
      <div className="crony-card-main">
        <div className="crony-card-head">
          <span className="crony-title">{job.title}</span>
          <span className={`status-pill ${job.pill}`}>
            <span className="dot" />
            {job.legacy ? 'Legacy' : PILL_LABEL[job.pill]}
          </span>
        </div>
        <div className="crony-meta">
          <span className={`space-pill ${job.space}`}>{SPACE_LABEL[job.space] ?? job.space}</span>
          <span className="crony-interval">{job.interval}</span>
          <span className="crony-db">{job.db}</span>
        </div>
        {job.description && <div className="crony-desc">{job.description}</div>}
        <div className="crony-foot">
          <span title={job.lastRun ?? ''}>Poslední běh: <strong>{relTime(job.lastRun)}</strong></span>
          {job.detail && <span className="crony-detail">{job.detail}</span>}
          {job.dataThrough && <span className="crony-detail">data do {job.dataThrough.slice(0, 10)}</span>}
        </div>
        {(job.pill === 'offline' || job.pill === 'unknown') && job.error && <div className="crony-error">{job.error}</div>}
      </div>
    </ArcPanel>
  );
}
export function CronyPage() {
  const [data, setData] = useState<CronyOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      setData(await fetchCrony());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Načtení selhalo');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  const spaces = ['rondo', 'affilmaster', 'all'];
  const jobsBySpace = (sp: string) => (data?.jobs ?? []).filter((j) => j.space === sp);

  return (
    <ArcPage className="crony-page">
      <ArcPageHeader
        eyebrow="Provoz Platform 1"
        title="Crony"
        description="Přehled automatických synchronizací a jejich aktuálního stavu"
        meta={<div className="crony-head-actions">
          {data && (
            <div className="crony-counts">
              {(['offline', 'warning', 'unknown', 'online'] as CronyPill[]).map(
                (p) => data.counts[p] > 0 && (
                  <span key={p} className={`status-pill ${p}`}><span className="dot" />{data.counts[p]} {PILL_LABEL[p]}</span>
                ),
              )}
            </div>
          )}
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={15} aria-hidden="true" />
            Obnovit
          </Button>
        </div>}
      />

      {loading && !data && <PageState title="Načítám synchronizace" loading />}
      {err && <PageState title="Synchronizace se nepodařilo načíst" message={err} />}
      {data && data.sourceErrors.length > 0 && (
        <ArcPanel className="crony-source-error" role="alert">
          Přehled není úplný, nedostupné zdroje: {data.sourceErrors.map((e) => e.db).join(', ')}. Úlohy z těchto databází jsou označeny jako neznámé.
        </ArcPanel>
      )}

      {data && spaces.map((sp) => {
        const jobs = jobsBySpace(sp);
        if (!jobs.length) return null;
        return (
          <section key={sp} className="crony-section">
            <h2 className="crony-section-title">{SPACE_LABEL[sp] ?? sp}</h2>
            <div className="crony-grid">
              {jobs.map((j) => <CronyCard key={j.id} job={j} />)}
            </div>
          </section>
        );
      })}

      {data && data.jobs.length === 0 && !loading && (
        <ArcPanel className="crony-empty">Žádné synchronizace k zobrazení.</ArcPanel>
      )}
    </ArcPage>
  );
}
